/**
 * Which e-commerce platform(s) a store is connected to, derived from its integrations.
 * Pages use this instead of hard-coding "Shopify" so a BigCommerce / Lightspeed /
 * WooCommerce store is never told to configure a Shopify integration.
 */

/** The minimal integration shape this helper needs; both the API config and dashboard DTO satisfy it. */
export interface PlatformIntegrationLike {
  id?: string;
  platform?: string;
  status?: string;
  isActive?: boolean;
  settings?: { syncPricing?: boolean } | null;
}

const PLATFORM_LABELS: Record<string, string> = {
  shopify: 'Shopify',
  bigcommerce: 'BigCommerce',
  lightspeed: 'Lightspeed',
  lightspeed_ecom: 'Lightspeed eCom (C-Series)',
  woocommerce: 'WooCommerce',
  magento: 'Magento',
  prestashop: 'PrestaShop',
  opencart: 'OpenCart',
};

/** Used when the store has no integration (or none we can name). */
export const GENERIC_PLATFORM_LABEL = 'your store';

/** Platforms whose integration can pull orders for the Sales and Rebate reports. */
const ORDER_SYNC_PLATFORMS = new Set(['shopify', 'lightspeed']);

export function platformLabel(platform: string | null | undefined): string {
  const key = String(platform || '').trim().toLowerCase();
  if (!key) return GENERIC_PLATFORM_LABEL;
  return PLATFORM_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Platforms whose display name is translated (a product name with a descriptive suffix) rather
 * than a bare brand name. `platformDisplayLabel` reads these; `platformLabel` keeps the English.
 */
const PLATFORM_LABEL_I18N_KEYS: Record<string, string> = {
  lightspeed_ecom: 'integrations.lightspeedEcom.platformName',
};

/**
 * Logo file under `public/logos/platforms/` for a platform whose slug is not its own file name.
 * Lightspeed eCom (C-Series) is a Lightspeed product and has no logo of its own.
 */
const PLATFORM_LOGO_SLUGS: Record<string, string> = {
  lightspeed_ecom: 'lightspeed',
};

/** The platform's display name, translated where the name carries copy (Lightspeed eCom). */
export function platformDisplayLabel(platform: string | null | undefined, t: (key: string) => string): string {
  const key = String(platform || '').trim().toLowerCase();
  const i18nKey = PLATFORM_LABEL_I18N_KEYS[key];
  return i18nKey ? t(i18nKey) : platformLabel(platform);
}

/** Logo path for a platform slug, mapping the slugs that share another platform's logo. */
export function platformLogo(platform: string | null | undefined): string {
  const key = String(platform || '').trim().toLowerCase();
  return `/logos/platforms/${PLATFORM_LOGO_SLUGS[key] || key}.svg`;
}

/** True when Motzu can sync sales orders from this platform. */
export function platformSupportsOrderSync(platform: string | null | undefined): boolean {
  return ORDER_SYNC_PLATFORMS.has(String(platform || '').trim().toLowerCase());
}

const isLive = (i: PlatformIntegrationLike): boolean =>
  i.status === 'active' || (i.status === undefined && i.isActive !== false);

/** "Shopify", "Shopify and BigCommerce", "Shopify, BigCommerce and Lightspeed". */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] || GENERIC_PLATFORM_LABEL;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

export interface StorePlatformInfo {
  /** Distinct platform keys, live integrations first. */
  platforms: string[];
  /** The platform of the first live integration (else the first integration), or null. */
  primaryPlatform: string | null;
  /** Label for the primary platform, or "your store" with no integration. */
  platformLabel: string;
  /** Labels for every connected platform, e.g. "Shopify and BigCommerce". */
  platformsLabel: string;
  /** Any integration at all, regardless of platform. */
  hasIntegration: boolean;
  hasShopify: boolean;
  /** True if at least one connected platform can sync orders. */
  hasOrderSyncPlatform: boolean;
}

export function deriveStorePlatform(integrations: PlatformIntegrationLike[] | null | undefined): StorePlatformInfo {
  const rows = (integrations || []).filter((i) => !!i.platform);
  const ordered = [...rows.filter(isLive), ...rows.filter((i) => !isLive(i))];
  const platforms = Array.from(new Set(ordered.map((i) => String(i.platform).toLowerCase())));
  const primaryPlatform = platforms[0] ?? null;
  return {
    platforms,
    primaryPlatform,
    platformLabel: platformLabel(primaryPlatform),
    platformsLabel: joinLabels(platforms.map((p) => platformLabel(p))),
    hasIntegration: (integrations || []).length > 0,
    hasShopify: platforms.includes('shopify'),
    hasOrderSyncPlatform: platforms.some((p) => platformSupportsOrderSync(p)),
  };
}
